// Public site pages — the quiet front door of a by-referral practice.
// A bare landing (logo + subtle top menu, no copy), About (what we do),
// and the TDI-Brooks case study. No selling; the console is the product.

import { markEl, markSvgFluid } from "../ui/mark.js";
import { waveField } from "../ui/waves.js";

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className !== undefined) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

export interface SiteNavOptions {
  authed: boolean;
  /** A SeaState principal rather than a client user. Only the admin frame reads
   *  it, and it is optional because the public pages have no business knowing.
   *  Absent means false — a page that forgets to pass it gets the safe answer. */
  staff?: boolean;
  navigate: (path: string) => void;
}

function navLink(label: string, path: string, opts: SiteNavOptions, cls?: string): HTMLAnchorElement {
  const a = el("a", cls, label);
  a.href = path;
  a.addEventListener("click", (e) => {
    e.preventDefault();
    opts.navigate(path);
  });
  return a;
}

/** Faint depth-contour texture — quiet nautical-chart ground, drawn once per resize. */
function contourCanvas(container: HTMLElement): void {
  const cv = document.createElement("canvas");
  cv.className = "contour-bg";
  container.appendChild(cv);
  const draw = (): void => {
    const w = cv.clientWidth;
    const h = cv.clientHeight;
    if (w <= 0 || h <= 0) return;
    const dpr = Math.min(window.devicePixelRatio > 0 ? window.devicePixelRatio : 1, 2);
    cv.width = Math.round(w * dpr);
    cv.height = Math.round(h * dpr);
    const g = cv.getContext("2d");
    if (g === null) return;
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    g.clearRect(0, 0, w, h);
    g.strokeStyle = "rgba(22, 35, 46, 0.10)";
    g.lineWidth = 1;
    const lines = Math.ceil(h / 40) + 3;
    for (let k = 0; k < lines; k++) {
      const base = k * 40 - 20;
      g.globalAlpha = 0.4 + 0.45 * Math.abs(Math.sin(k * 1.3));
      g.beginPath();
      for (let x = 0; x <= w; x += 8) {
        const y = base + 18 * Math.sin(x / 190 + k * 0.5) + 8 * Math.sin(x / 72 + k);
        if (x === 0) g.moveTo(x, y);
        else g.lineTo(x, y);
      }
      g.stroke();
    }
    g.globalAlpha = 1;
  };
  const ro = new ResizeObserver(draw);
  ro.observe(container);
  draw();
}

function publicNav(opts: SiteNavOptions, current: string): HTMLElement {
  const nav = el("nav", "pubnav");
  const brand = el("a", "brand");
  brand.href = "/";
  brand.appendChild(markEl(20));
  brand.appendChild(document.createTextNode("SeaState"));
  brand.addEventListener("click", (e) => {
    e.preventDefault();
    opts.navigate("/");
  });
  nav.appendChild(brand);
  const links = el("div", "pubnav-links");
  if (current !== "/about") links.appendChild(navLink("About", "/about", opts));
  if (current !== "/case-study") links.appendChild(navLink("Case study", "/case-study", opts));
  links.appendChild(
    opts.authed
      ? navLink("Console →", "/console", opts, "loginlink")
      : navLink("Client login", "/login", opts, "loginlink"),
  );
  nav.appendChild(links);
  return nav;
}

// --- landing --------------------------------------------------------------------

export function mountLanding(container: HTMLElement, opts: SiteNavOptions): void {
  document.title = "SeaState — Bespoke Maritime Intelligence";
  const bare = el("div", "bare");
  if (!waveField(bare)) contourCanvas(bare); // WebGL waves; static contours as fallback

  const top = el("div", "bare-top");
  const menu = el("nav", "bare-menu");
  menu.appendChild(navLink("About", "/about", opts));
  menu.appendChild(navLink("Case study", "/case-study", opts));
  menu.appendChild(
    opts.authed
      ? navLink("Console →", "/console", opts, "login")
      : navLink("Client login", "/login", opts, "login"),
  );
  top.appendChild(menu);
  bare.appendChild(top);

  const center = el("div", "bare-center");
  const lockup = el("div", "lockup");
  // Inlined rather than <img src="/logo.svg">: it is the same vector the header
  // draws, so the two can never drift, it inherits colour from CSS, and there is
  // no second request for the one thing above the fold.
  const logo = el("div", "lockup-mark");
  logo.innerHTML = markSvgFluid();
  logo.setAttribute("role", "img");
  logo.setAttribute("aria-label", "SeaState");
  lockup.appendChild(logo);
  lockup.appendChild(el("div", "lockup-word", "SEASTATE"));
  lockup.appendChild(el("div", "lockup-tag", "Bespoke Maritime Intelligence"));
  center.appendChild(lockup);
  bare.appendChild(center);

  container.appendChild(bare);
}

// --- doc-style pages (About, Case study) ---------------------------------------

interface DocSection {
  heading: string;
  paras: string[];
  callout?: string;
}

function docPage(
  container: HTMLElement,
  opts: SiteNavOptions,
  current: string,
  eyebrow: string,
  headline: string,
  lede: string,
  sections: DocSection[],
): void {
  const page = el("div", "site-page");
  page.appendChild(publicNav(opts, current));

  const lead = el("div", "lead");
  lead.appendChild(el("span", "eyebrow", eyebrow));
  lead.appendChild(el("h2", undefined, headline));
  lead.appendChild(el("p", undefined, lede));
  page.appendChild(lead);

  const doc = el("div", "doc");
  const aside = el("aside");
  const article = el("article");
  for (const s of sections) {
    const id = s.heading.toLowerCase().replace(/[^a-z0-9]+/g, "-");
    const a = el("a", undefined, s.heading);
    a.href = `#${id}`;
    aside.appendChild(a);
    const h = el("h3", undefined, s.heading);
    h.id = id;
    article.appendChild(h);
    for (const p of s.paras) article.appendChild(el("p", undefined, p));
    if (s.callout !== undefined) article.appendChild(el("div", "callout", s.callout));
  }
  doc.append(aside, article);
  page.appendChild(doc);

  const foot = el("footer", "sitefoot");
  foot.appendChild(el("span", undefined, "SeaState · a Deep Blue Analysis project"));
  foot.appendChild(el("span", "right", "© 2026 · By referral · Not for navigation"));
  page.appendChild(foot);

  container.appendChild(page);
}

export function mountAbout(container: HTMLElement, opts: SiteNavOptions): void {
  document.title = "About — SeaState";
  docPage(
    container,
    opts,
    "/about",
    "About",
    "We build a marine-forecast console around one operator.",
    "SeaState takes a single idea — click anywhere at sea, read the window ahead — and shapes it into a decision tool for a specific survey operator: its vessels, its operations, and the limits it works to. We don't sell a signup. We meet, and design the console around how you actually work.",
    [
      {
        heading: "What we do",
        paras: [
          "One point-and-click console. Click a spot at sea and it draws the multi-model wind and wave forecast for that exact point, with an honest read on confidence. Then it goes further than a weather map: it knows your vessels and your operations, so the same forecast becomes a go / marginal / no-go read for the job you're actually planning — coring, CPT, survey, transfer.",
        ],
      },
      {
        heading: "How we work",
        paras: [
          "An engagement begins with a conversation, not a sign-up form. We enter your vessels from their spec sheets, set your operations and their working limits with your people, and scope the map to the theaters you mobilize into. Then we calibrate against your real experience and keep the model current as your work changes. Clients come to us by referral.",
        ],
        callout:
          "The value is as much in what's left out as what's shown: the data you need to make the call, and none of the data you don't.",
      },
      {
        heading: "The science",
        paras: [
          "SeaState keeps the world's operational models — NOAA GFS and GFS-Waves with the 31-member GEFS ensemble, ECMWF, DWD, Météo-France — side by side rather than blending them into one confident-looking line. The ensemble band shows how settled the atmosphere is. Every figure is computed from real data or clearly labelled provisional; the estimates say so.",
        ],
      },
      {
        heading: "The team",
        paras: [
          "Dr. Les Bender — oceanographer and President of Deep Blue Analysis: the metocean lead, with decades of offshore forecasting judgment behind the thresholds the console reasons about.",
          "Cody Handlin — programmer and bespoke web-engine design: the point-and-click console, the multi-model forecast pipeline, and the operator-specific tooling.",
        ],
      },
    ],
  );
}

export function mountCaseStudy(container: HTMLElement, opts: SiteNavOptions): void {
  document.title = "Case study — SeaState";
  docPage(
    container,
    opts,
    "/case-study",
    "Case study",
    "A console built for TDI-Brooks.",
    "TDI-Brooks International runs offshore survey and geotechnical campaigns across the Gulf of Mexico, the Caribbean and West Africa with a small set of research vessels. They came to us to replace a static forecast product — and got a console shaped entirely around how they work.",
    [
      {
        heading: "Their vessels",
        paras: [
          "All six vessels entered from their official spec sheets — from the 75 m DP2 Nautilus with its moonpool CPT rig down to the 8 m Barnacle, a daylight-only nearshore survey boat. Each carries its own dimensions, draft and roll behaviour, and — critically — only the operations that hull actually performs.",
        ],
      },
      {
        heading: "Their operations",
        paras: [
          "Jumbo piston coring, CPT push, multibeam survey, crew transfer — each scoped per vessel, each with its own wind, wave and duration limits. A seeded default is always present, and the console says so plainly until their team sets its own numbers.",
        ],
      },
      {
        heading: "The right data",
        paras: [
          "Multi-model wind and waves with the GEFS ensemble band, seabed depth and relief at the point, daylight and visibility, squall risk. What was left out matters as much: no clutter, no metrics their operations team doesn't act on. The console shows what they need to make the call, and stays quiet about the rest.",
        ],
      },
      {
        heading: "Their overlays",
        paras: [
          "The map carries the boundaries that govern their work — US BOEM oil-gas and offshore-wind leases, the EEZ and disputed-zone lines across the Guyana–Suriname basin and West Africa, protected areas and seasonal closures — scoped to the theaters they mobilize into. A different operator would see a different map.",
        ],
        callout:
          "A bespoke console answers the questions this operator asks, in their vocabulary, and stays quiet about everything else.",
      },
      {
        heading: "The outcome",
        paras: [
          "Their planners open one point-and-click console instead of stitching together a generic weather app, a chart plotter and a spreadsheet — and read the window the way their operations team already thinks about it.",
        ],
      },
    ],
  );
}
